import { useState, useEffect, useRef, useCallback } from "react";
import { SEAPORTS, AIRPORTS } from "../data/ports";
import { CITIES_BY_COUNTRY, COUNTRIES } from "../data/locations";
import { Search, MapPin, Ship, Plane, Building2, ChevronDown, X, Ban, Edit3 } from "lucide-react";

// Special options that are always available
const SPECIAL_OPTIONS = [
  {
    id: 'special-na',
    type: 'special',
    code: 'N/A',
    name: 'N/A - not available',
    country: '',
    displayName: 'N/A - not available',
    fullLabel: 'N/A - not available',
    searchTerms: ['n/a', 'not available', 'na']
  },
  {
    id: 'special-other',
    type: 'other',
    code: null,
    name: 'Other (custom location)',
    country: '',
    displayName: 'Other - enter custom location',
    fullLabel: 'Other - enter custom location',
    searchTerms: ['other', 'custom', 'free text', 'manual']
  }
];

// Build a unified location database
const buildLocationDatabase = () => {
  const locations = [];
  
  // Add seaports with type
  SEAPORTS.forEach(port => {
    locations.push({
      id: `port-${port.code}`,
      type: 'seaport',
      code: port.code,
      name: port.name,
      country: port.country,
      displayName: `${port.name}, ${port.country}`,
      fullLabel: `${port.name} (${port.code}) - ${port.country}`,
      searchTerms: [port.name.toLowerCase(), port.code.toLowerCase(), port.country.toLowerCase()]
    });
  });
  
  // Add airports with type
  if (AIRPORTS) {
    AIRPORTS.forEach(airport => {
      locations.push({
        id: `airport-${airport.code}`,
        type: 'airport',
        code: airport.code,
        name: airport.name,
        country: airport.country,
        displayName: `${airport.name}, ${airport.country}`,
        fullLabel: `${airport.name} (${airport.code}) - ${airport.country}`,
        searchTerms: [airport.name.toLowerCase(), airport.code.toLowerCase(), airport.country.toLowerCase()]
      });
    });
  }
  
  // Add cities that don't have ports
  Object.entries(CITIES_BY_COUNTRY).forEach(([country, cities]) => {
    cities.forEach(city => {
      // Check if this city already exists as a seaport (we still want to add it as a city for non-port searches)
      const existsAsSeaport = locations.some(
        loc => loc.type === 'seaport' && loc.name.toLowerCase() === city.toLowerCase() && loc.country === country
      );
      // Always add city as a separate entry (even if it exists as airport/port)
      // This ensures cities show up in all search modes
      if (!existsAsSeaport) {
        locations.push({
          id: `city-${country}-${city}`,
          type: 'city',
          code: null,
          name: city,
          country: country,
          displayName: `${city}, ${country}`,
          fullLabel: `${city}, ${country}`,
          searchTerms: [city.toLowerCase(), country.toLowerCase()]
        });
      }
    });
  });
  
  return locations;
};

const LOCATION_DATABASE = buildLocationDatabase();

// Full-world backend place search (/api/places/search). Maps results into the
// same location shape the components render. `showSeaport/showAirport` pick the
// dataset slice (sea = UN/LOCODE ports+inland, air = IATA airports).
const fetchBackendPlaces = async (query, { showSeaport = true, showAirport = false } = {}) => {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  const kind = showAirport && !showSeaport ? "air" : (showAirport && showSeaport ? "all" : "sea");
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const tk = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const res = await fetch(`${base}/api/places/search?q=${encodeURIComponent(q)}&kind=${kind}&limit=12`, {
      headers: tk ? { Authorization: `Bearer ${tk}` } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.results || []).map((r) => ({
      id: `be-${r.code_type}-${r.code}`,
      type: r.code_type === "iata" ? "airport" : "seaport",
      code: r.code,
      name: r.name,
      country: r.country_name || r.country || "",
      displayName: `${r.name}, ${r.country_name || r.country || ""}`,
      fullLabel: r.label || `${r.name} (${r.code})`,
      searchTerms: [String(r.name || "").toLowerCase(), String(r.code || "").toLowerCase()],
      _backend: true,
    }));
  } catch (e) {
    return [];
  }
};

// Search function
const searchLocations = (query, options = {}) => {
  const { types = ['seaport', 'airport', 'city'], country = null, limit = 15, includeSpecial = true } = options;
  
  if (!query || query.length < 1) {
    // Return special options when no query
    return includeSpecial ? [...SPECIAL_OPTIONS] : [];
  }
  
  const lowerQuery = query.toLowerCase();
  
  // Check if query matches special options
  const matchingSpecial = includeSpecial ? SPECIAL_OPTIONS.filter(opt =>
    opt.searchTerms.some(term => term.includes(lowerQuery))
  ) : [];
  
  let results = LOCATION_DATABASE.filter(loc => {
    // Filter by type
    if (!types.includes(loc.type)) return false;
    
    // Filter by country if specified
    if (country && loc.country !== country) return false;
    
    // Search by terms
    return loc.searchTerms.some(term => term.startsWith(lowerQuery)) ||
           loc.searchTerms.some(term => term.includes(lowerQuery));
  });
  
  // Sort: exact matches first, then ports before cities
  results.sort((a, b) => {
    const aExact = a.name.toLowerCase().startsWith(lowerQuery);
    const bExact = b.name.toLowerCase().startsWith(lowerQuery);
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;
    
    // Ports before cities
    if (a.type === 'seaport' && b.type !== 'seaport') return -1;
    if (a.type !== 'seaport' && b.type === 'seaport') return 1;
    
    return a.name.localeCompare(b.name);
  });
  
  // Add special options at the end if they matched, or always add "Other" option
  const regularResults = results.slice(0, limit - 2);
  
  // Always include matching special options + "Other" if query has no results
  if (regularResults.length === 0) {
    // No results found - show special options prominently
    return [...matchingSpecial, ...SPECIAL_OPTIONS.filter(s => !matchingSpecial.includes(s))];
  }
  
  // Add special options at the end
  return [...regularResults, ...matchingSpecial.filter(s => !regularResults.some(r => r.id === s.id))];
};

// Get icon for location type
const LocationIcon = ({ type, className = "h-4 w-4" }) => {
  switch (type) {
    case 'seaport':
      return <Ship className={`${className} text-blue-500`} />;
    case 'airport':
      return <Plane className={`${className} text-purple-500`} />;
    case 'city':
      return <Building2 className={`${className} text-gray-500`} />;
    case 'special':
      return <Ban className={`${className} text-orange-500`} />;
    case 'other':
      return <Edit3 className={`${className} text-green-500`} />;
    default:
      return <MapPin className={`${className} text-gray-500`} />;
  }
};

/**
 * Smart Location Autocomplete Component
 * 
 * Features:
 * - Unified search across ports, airports, and cities
 * - Auto-fills country when location is selected
 * - Shows UN/LOCODE for ports
 * - Supports filtering by location types
 */
export function SmartLocationInput({
  value = {},
  onChange,
  placeholder = "Search city, port, or airport...",
  types = ['seaport', 'airport', 'city'],
  showSeaport = true,
  showAirport = false,
  label = "Location",
  required = false,
  className = "",
  cmaSearch = false,
  "data-testid": dataTestId,
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customLocation, setCustomLocation] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Determine which types to search
  const searchTypes = [];
  if (showSeaport) searchTypes.push('seaport');
  if (showAirport) searchTypes.push('airport');
  searchTypes.push('city');

  // Initialize query from value
  useEffect(() => {
    if (value.city || value.port_name) {
      setQuery(value.port_name || value.city || "");
      // Check if it's a custom entry
      if (value.location_type === 'custom' || value.location_type === 'other') {
        setShowCustomInput(false); // Don't show input if value is already set
      }
    }
  }, [value.city, value.port_name, value.location_type]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Live CMA place search (any port / inland pre-carriage point) — merged into
  // the local suggestions. Debounced; silently ignores quota/network errors.
  useEffect(() => {
    if (!cmaSearch || !query || query.length < 2) return;
    const t = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const tk = localStorage.getItem("token");
        const res = await fetch(`${base}/api/cma-cgm/places?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const cmaItems = (data.places || []).filter((p) => p.code).map((p) => ({
          id: `cma-${p.code}`,
          type: "seaport",
          code: p.code,
          name: p.name || p.code,
          country: p.country || "",
          displayName: `${p.name || p.code}${p.country ? ", " + p.country : ""}`,
          fullLabel: `${p.name || p.code} (${p.code})`,
          searchTerms: [String(p.name || "").toLowerCase(), String(p.code).toLowerCase()],
          _cma: true,
        }));
        setSuggestions((prev) => {
          const codes = new Set(prev.map((s) => s.code).filter(Boolean));
          return [...prev, ...cmaItems.filter((ci) => !codes.has(ci.code))];
        });
      } catch (e) {
        /* ignore */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, cmaSearch]);

  // Full-world backend place search — prepends valid code-carrying options so
  // any city/port resolves to a UN/LOCODE / IATA (no unmatched free text).
  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(async () => {
      const be = await fetchBackendPlaces(query, { showSeaport, showAirport });
      if (!be.length) return;
      setSuggestions((prev) => {
        const codes = new Set(be.map((b) => b.code));
        const rest = prev.filter((s) => !s.code || !codes.has(s.code));
        const specials = rest.filter((s) => s.type === "special" || s.type === "other");
        const locals = rest.filter((s) => s.type !== "special" && s.type !== "other");
        return [...be, ...locals, ...specials];
      });
      setShowSuggestions(true);
    }, 250);
    return () => clearTimeout(t);
  }, [query, showSeaport, showAirport]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    setShowCustomInput(false);
    
    if (inputValue.length >= 1) {
      const results = searchLocations(inputValue, { types: searchTypes });
      setSuggestions(results);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } else {
      // Show special options even with empty query
      setSuggestions(SPECIAL_OPTIONS);
      setShowSuggestions(true);
    }
  };

  const handleSelect = (location) => {
    // Handle "Other" selection - show custom input
    if (location.type === 'other') {
      setShowCustomInput(true);
      setShowSuggestions(false);
      setCustomLocation(query); // Pre-fill with what user typed
      return;
    }
    
    setQuery(location.name);
    setShowSuggestions(false);
    setSuggestions([]);
    setShowCustomInput(false);
    
    // Build the complete location object
    const newValue = {
      city: location.name,
      country: location.country,
      port_code: location.type === 'seaport' ? location.code : (location.type === 'special' ? 'N/A' : null),
      port_name: location.type === 'seaport' ? location.name : null,
      airport_code: location.type === 'airport' ? location.code : null,
      airport_name: location.type === 'airport' ? location.name : null,
      location_type: location.type,
    };
    
    onChange(newValue);
  };

  const handleCustomSubmit = () => {
    if (customLocation.trim()) {
      setQuery(customLocation);
      setShowCustomInput(false);
      onChange({
        city: customLocation,
        country: "",
        port_code: null,
        port_name: null,
        airport_code: null,
        airport_name: null,
        location_type: 'custom',
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setQuery("");
    setShowCustomInput(false);
    setCustomLocation("");
    onChange({
      city: "",
      country: "",
      port_code: null,
      port_name: null,
      airport_code: null,
      airport_name: null,
      location_type: null,
    });
  };

  return (
    <div ref={wrapperRef} className={`relative space-y-2 ${className}`}>
      {/* Custom Location Input */}
      {showCustomInput ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
              placeholder="Enter custom location..."
              className="flex-1 px-3 py-2.5 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm bg-green-50"
              autoFocus
            />
            <button
              type="button"
              onClick={handleCustomSubmit}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCustomInput(false);
                setCustomLocation("");
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Enter a custom location name (city, port, or place not in our database)
          </p>
        </div>
      ) : (
        <>
          {/* Main Input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // Always show options on focus
                const results = query.length >= 1 
                  ? searchLocations(query, { types: searchTypes })
                  : SPECIAL_OPTIONS;
                setSuggestions(results);
                setShowSuggestions(true);
              }}
              placeholder={placeholder}
              data-testid={dataTestId}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              required={required}
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
              {suggestions.map((location, index) => (
                <div
                  key={location.id}
                  onClick={() => handleSelect(location)}
                  className={`px-3 py-2.5 cursor-pointer transition-colors flex items-center gap-3 ${
                    index === selectedIndex
                      ? "bg-blue-50 border-l-2 border-blue-500"
                      : location.type === 'special' || location.type === 'other'
                        ? "hover:bg-amber-50 border-t border-gray-100"
                        : "hover:bg-gray-50"
                  }`}
                >
                  <LocationIcon type={location.type} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {location.name}
                      {location.code && location.type !== 'special' && (
                        <span className="ml-2 text-xs text-blue-600 font-mono bg-blue-50 px-1.5 py-0.5 rounded">
                          {location.code}
                        </span>
                      )}
                    </div>
                    {location.country && <div className="text-xs text-gray-500">{location.country}</div>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    location.type === 'seaport' ? 'bg-blue-100 text-blue-700' :
                    location.type === 'airport' ? 'bg-purple-100 text-purple-700' :
                    location.type === 'special' ? 'bg-orange-100 text-orange-700' :
                    location.type === 'other' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {location.type === 'seaport' ? 'Port' : 
                     location.type === 'airport' ? 'Airport' :
                     location.type === 'special' ? 'N/A' :
                     location.type === 'other' ? 'Custom' : 'City'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Display selected location info */}
      {value.country && (
        <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
          <MapPin className="h-3 w-3" />
          <span className="font-medium">{value.city}, {value.country}</span>
          {value.port_code && (
            <span className="ml-auto flex items-center gap-1">
              <Ship className="h-3 w-3 text-blue-500" />
              <span className="font-mono text-blue-600">{value.port_code}</span>
            </span>
          )}
          {value.airport_code && (
            <span className="ml-auto flex items-center gap-1">
              <Plane className="h-3 w-3 text-purple-500" />
              <span className="font-mono text-purple-600">{value.airport_code}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for forms - single line with all info
 */
export function SmartLocationField({
  label = "Location",
  value = {},
  onChange,
  showSeaport = true,
  showAirport = false,
  required = false,
  error = null,
  "data-testid": dataTestId,
}) {
  const [query, setQuery] = useState(value.city || value.port_name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const searchTypes = [];
  if (showSeaport) searchTypes.push('seaport');
  if (showAirport) searchTypes.push('airport');
  searchTypes.push('city');

  useEffect(() => {
    if (value.city || value.port_name) {
      setQuery(value.port_name || value.city || "");
    }
  }, [value.city, value.port_name]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Full-world backend place search merged into the dropdown.
  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(async () => {
      const be = await fetchBackendPlaces(query, { showSeaport, showAirport });
      if (!be.length) return;
      setSuggestions((prev) => {
        const codes = new Set(be.map((b) => b.code));
        return [...be, ...prev.filter((s) => !s.code || !codes.has(s.code))];
      });
      setShowDropdown(true);
    }, 250);
    return () => clearTimeout(t);
  }, [query, showSeaport, showAirport]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    
    if (inputValue.length >= 1) {
      const results = searchLocations(inputValue, { types: searchTypes });
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      // Clear the selection
      onChange({
        city: "",
        country: "",
        port_code: null,
        port_name: null,
        airport_code: null,
        airport_name: null,
      });
    }
  };

  // Handle when user leaves the field without selecting from dropdown
  const handleBlur = () => {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      if (query && !value.city) {
        // User typed something but didn't select from dropdown
        // Allow the free text as city name
        onChange({
          city: query,
          country: "", // Will need manual entry or we can try to infer
          port_code: null,
          port_name: null,
          airport_code: null,
          airport_name: null,
          freeText: true, // Flag to indicate this was a free text entry
        });
      }
    }, 200);
  };

  const handleSelect = useCallback((location) => {
    setQuery(location.name);
    setShowDropdown(false);
    
    onChange({
      city: location.name,
      country: location.country,
      port_code: location.type === 'seaport' ? location.code : null,
      port_name: location.type === 'seaport' ? location.name : null,
      airport_code: location.type === 'airport' ? location.code : null,
      airport_name: location.type === 'airport' ? location.name : null,
      location_type: location.type,
    });
  }, [onChange]);

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (query.length >= 1) {
              const results = searchLocations(query, { types: searchTypes });
              setSuggestions(results);
              setShowDropdown(results.length > 0);
            }
          }}
          placeholder="Type city or port name..."
          data-testid={dataTestId}
          className={`w-full px-3 py-2 pr-20 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-300 bg-red-50' : 
            value.country ? 'border-emerald-300 bg-emerald-50/30' : 
            value.city ? 'border-amber-300 bg-amber-50/30' : 'border-gray-300'
          }`}
          required={required}
        />
        
        {/* Country & Port code display */}
        {value.country && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1.5">
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {value.country.slice(0, 3).toUpperCase()}
            </span>
            {value.port_code && (
              <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded font-mono">
                {value.port_code}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((location, index) => (
            <div
              key={location.id}
              onClick={() => handleSelect(location)}
              className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                index === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50"
              }`}
            >
              <LocationIcon type={location.type} className="h-4 w-4" />
              <div className="flex-1">
                <span className="font-medium text-sm">{location.name}</span>
                {location.code && (
                  <span className="ml-2 text-xs font-mono text-blue-600">({location.code})</span>
                )}
              </div>
              <span className="text-xs text-gray-500">{location.country}</span>
            </div>
          ))}
        </div>
      )}
      
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

export default SmartLocationInput;
